import { DateTime } from "luxon";
import type { ReportUploadedFile, ReportUploadKind } from "@/api/report/_models/ReportUpload";
import type { SnapshotUploadFile } from "./useSnapshotUpload";

/** The zone the exports are stamped in. */
export const BUSINESS_TIME_ZONE = "Australia/Sydney";

/** Today in Sydney, the day the exports are stamped in. */
export const todayInSydney = (): DateTime => {
  return DateTime.now().setZone(BUSINESS_TIME_ZONE).startOf("day");
};

/** The URL query param holding a Snapshot page's day (?date=2026-10-27). */
export const SNAPSHOT_DAY_PARAM = "date";
const SNAPSHOT_DAY_STORAGE_KEY = "subemo.snapshot.day";
const SNAPSHOT_DAY_FORMAT = "yyyy-MM-dd";

/** A yyyy-MM-dd day in Sydney; null when empty, unreadable or in the future. */
export const toSnapshotDayFromText = (text: string | null): DateTime | null => {
  if (!text) return null;
  const day = DateTime.fromFormat(text, SNAPSHOT_DAY_FORMAT, { zone: BUSINESS_TIME_ZONE });
  if (!day.isValid || day > todayInSydney()) return null;
  return day;
};

export const toSnapshotDayText = (day: DateTime): string => day.toFormat(SNAPSHOT_DAY_FORMAT);

/** The last day picked on a Snapshot page, from localStorage; null when none or storage is off. */
export const getStoredSnapshotDay = (): DateTime | null => {
  try {
    return toSnapshotDayFromText(window.localStorage.getItem(SNAPSHOT_DAY_STORAGE_KEY));
  }
  catch {
    return null;
  }
};

/** Remembers the day picked in localStorage (silently skipped when storage is off). */
export const storeSnapshotDay = (day: DateTime) => {
  try {
    window.localStorage.setItem(SNAPSHOT_DAY_STORAGE_KEY, toSnapshotDayText(day));
  }
  catch {
  }
};

/** The day a Snapshot page opens on: the URL's, else the stored one, else today. */
export const toInitialSnapshotDay = (search: string): DateTime => {
  const params = new URLSearchParams(search);
  const fromUrl = toSnapshotDayFromText(params.get(SNAPSHOT_DAY_PARAM));
  if (fromUrl) return fromUrl;
  return getStoredSnapshotDay() ?? todayInSydney();
};

/** Puts the day in the URL with history.replaceState, so stepping days doesn't pile up history. */
export const replaceSnapshotDayInUrl = (day: DateTime) => {
  const url = new URL(window.location.href);
  url.searchParams.set(SNAPSHOT_DAY_PARAM, toSnapshotDayText(day));
  window.history.replaceState(window.history.state, "", url);
};

/** yyMMdd, the date format the upload and snapshot endpoints take. */
export const toYyMmDd = (date: DateTime): string => {
  return date.toFormat("yyMMdd");
};

/** A readable message from an RTK Query / fetch error or any thrown value. */
export const toErrorText = (e: unknown): string => {
  const data = (e as { data?: { error?: unknown } })?.data;
  if (data && typeof data.error === "string") return data.error;
  const error = (e as { error?: unknown })?.error;
  if (typeof error === "string") return error;
  if (e instanceof Error) return e.message;
  return JSON.stringify(e);
};

/** The background job kind that builds a report's snapshot for a day. */
export const SNAPSHOT_BUILD_JOB_KIND = "buildSnapshot";

/** A build job's subject (`<report>/<yyMMdd>`), how a section finds its own latest build. */
export const toSnapshotBuildSubject = (report: ReportUploadKind, date: string): string => `${report}/${date}`;

/**
 * A section's files as it lists them, newest on top: those dropped here, then the day's other S3
 * files. A file uploaded here keeps its own row; its S3 entry is matched to it by key.
 */
export interface SnapshotSectionFiles {
  droppedFiles: SnapshotUploadFile[];
  otherFiles: ReportUploadedFile[];
  /** The day's S3 files by key. */
  fileByKey: Map<string, ReportUploadedFile>;
  fileCount: number;
  /** When the newest of the day's files was uploaded (ISO), or null. */
  latestUploadAt: string | null;
}

/** Merges the day's S3 files with those dropped here into the section's list. */
export const toSnapshotSectionFiles = (existing: ReportUploadedFile[], files: SnapshotUploadFile[]): SnapshotSectionFiles => {
  const fileByKey = new Map(existing.map((file) => [file.key, file]));
  const localKeys = new Set(files.flatMap((item) => (item.key ? [item.key] : [])));
  const otherFiles = existing
    .filter((file) => !localKeys.has(file.key))
    .sort((a, b) => b.key.localeCompare(a.key));
  const droppedFiles = [...files].reverse();
  const uploadedHereCount = files.filter((item) => item.status === "uploaded" && item.key && !fileByKey.has(item.key)).length;
  const fileCount = existing.length + uploadedHereCount;
  const uploadTimes = [
    ...existing.map((file) => file.uploadedAt),
    ...files.flatMap((item) => (item.uploadedAt ? [item.uploadedAt] : [])),
  ];
  let latestUploadAt: string | null = null;
  for (const time of uploadTimes) {
    if (latestUploadAt === null || time > latestUploadAt) latestUploadAt = time;
  }
  return { droppedFiles, otherFiles, fileByKey, fileCount, latestUploadAt };
};

/**
 * Where a report's section stands, in order of precedence: building (starting or running), failed
 * (failed or refused), stale (a file uploaded after the build), ok (built, nothing since), none
 * (files but no snapshot), empty (no files, no snapshot).
 */
export type SnapshotSectionStatus = "building" | "failed" | "stale" | "ok" | "none" | "empty";

export interface SnapshotSectionStatusInput {
  isBuilding: boolean;
  buildError: string | null;
  /** When the day's snapshot was built (ISO); null = not built. */
  builtAt: string | null;
  fileCount: number;
  /** When the newest of the day's files was uploaded (ISO), or null. */
  latestUploadAt: string | null;
}

/** Where a section stands (see SnapshotSectionStatus). */
export const toSnapshotSectionStatus = (input: SnapshotSectionStatusInput): SnapshotSectionStatus => {
  if (input.isBuilding) return "building";
  if (input.buildError) return "failed";
  if (input.builtAt) {
    if (input.latestUploadAt !== null && input.latestUploadAt > input.builtAt) return "stale";
    return "ok";
  }
  if (input.fileCount > 0) return "none";
  return "empty";
};

/** What a section tells the page about itself, for "Build all" and the pipeline. */
export interface SnapshotSectionSummary {
  label: string;
  fileCount: number;
  status: SnapshotSectionStatus;
  canBuild: boolean;
  /** Can be built now and isn't current (none, stale or failed). */
  needsBuild: boolean;
}

/** A same-named file's tag: the newest upload of that name, or an older one. */
export type SnapshotDuplicateTag = "newest" | "older";

/** The day's files that share a name: how many, and each one's tag by key. */
export interface SnapshotSectionDuplicates {
  count: number;
  tagByKey: Map<string, SnapshotDuplicateTag>;
}

/** Finds the day's files that share a name and tags each as the newest or an older upload. */
export const toSnapshotSectionDuplicates = (files: ReportUploadedFile[]): SnapshotSectionDuplicates => {
  const byName = new Map<string, ReportUploadedFile[]>();
  for (const file of files) {
    const named = byName.get(file.filename) ?? [];
    named.push(file);
    byName.set(file.filename, named);
  }
  const tagByKey = new Map<string, SnapshotDuplicateTag>();
  let count = 0;
  for (const named of byName.values()) {
    if (named.length < 2) continue;
    count += named.length;
    const newest = named.reduce((a, b) => {
      if (b.uploadedAt > a.uploadedAt) return b;
      if (b.uploadedAt === a.uploadedAt && b.key > a.key) return b;
      return a;
    });
    for (const file of named) {
      tagByKey.set(file.key, file.key === newest.key ? "newest" : "older");
    }
  }
  return { count, tagByKey };
};
